/**
 * The Y range a pane should show, computed from the bars actually in the window.
 *
 * **This replaces SciChart's `EAutoRange.Always` on price axes, and the reason is
 * one bar.** A candle whose index sits inside the X window was still left out of
 * the auto-ranged extent: measured on ARMK, the axis stopped at 58.68 — the
 * highest high of every bar *except* the last — while the last bar's high was
 * 61.52 and its index (2229) was comfortably inside a window running to 2242.98.
 * The gap-up bar drew with roughly a fifth of it on screen and the rest above the
 * pane, at every zoom level.
 *
 * Nothing in the range is guessed here: it is the extremes of the values in the
 * window, so a bar that is drawn is a bar that fits, by construction.
 *
 * Pure, and separate from the SciChart shell for the reason the rest of `plan/`
 * is: this is the part with the edge cases — an empty window, a series shorter
 * than the window, a column of nulls — and none of them need WebGL to exercise.
 */

/** One series' contribution to a pane's Y extent. */
export interface YSeriesValues {
  /** Upper edge per bar — highs for a candle, the value itself otherwise. */
  highs: readonly number[];
  /** Lower edge per bar. Same array as `highs` for a single-valued series. */
  lows: readonly number[];
}

export interface YRange {
  min: number;
  max: number;
}

/**
 * Fraction of the extent left clear above and below.
 *
 * A candle touching the top pixel of its pane reads as clipped even when it is
 * whole, which is the complaint this whole change came from — so the padding is
 * not decoration.
 */
export const Y_FIT_PADDING = 0.05;

/**
 * The range covering every finite value between `from` and `to` inclusive.
 *
 * `undefined` when the window holds nothing finite — a pane scrolled past the
 * end of its data, or a series that is all nulls. The caller leaves the axis
 * alone rather than writing a degenerate range, because an axis of `[0, 0]`
 * collapses the gridlines and takes the labels with it.
 *
 * **Indices are clamped, not trusted.** A window may legitimately run past both
 * ends of the data: `windowEndingAt` does not clamp its `min` at zero, and the
 * right margin puts `max` beyond the last bar on purpose.
 */
export function fitYRange(
  series: readonly YSeriesValues[],
  from: number,
  to: number,
  padding = Y_FIT_PADDING,
): YRange | undefined {
  let min = Infinity;
  let max = -Infinity;

  for (const s of series) {
    const start = Math.max(0, Math.floor(from));
    const end = Math.min(s.highs.length - 1, Math.ceil(to));
    for (let i = start; i <= end; i++) {
      const hi = s.highs[i];
      const lo = s.lows[i];
      if (hi !== undefined && Number.isFinite(hi) && hi > max) max = hi;
      if (lo !== undefined && Number.isFinite(lo) && lo < min) min = lo;
    }
  }

  if (!Number.isFinite(min) || !Number.isFinite(max)) return undefined;

  // A flat series has no extent to take a fraction of, so the padding is
  // absolute there. Without it the range is `[v, v]` and the axis collapses.
  const extent = max - min;
  const pad = extent > 0 ? extent * padding : Math.abs(max) * padding || 1;
  return { min: min - pad, max: max + pad };
}

/**
 * The index range a Y fit should actually read, given where the bars stop.
 *
 * **The axis is about the bars, not about anything drawn past them.** A frame
 * can now run beyond its last bar — a seasonal projection is drawn on dates the
 * symbol has no bar for — and those values are a forecast, not a price that
 * happened. Letting them range the axis means adding an overlay rescales the
 * candles underneath it: a cone reaching thirty per cent above the last close
 * pushes the bars into the bottom third of the pane, and nothing on screen says
 * why.
 *
 * `undefined` when the window holds no bars at all — scrolled entirely into the
 * projection — and the caller then leaves the axis alone. Freezing it is the
 * right answer there for the same reason: the scale is still the scale of the
 * price, which is what the reader is judging the forecast against.
 *
 * Separate from {@link fitYRange}'s own clamp, which is about an array being
 * shorter than the window. This one is about a *value* being past the edge of
 * what the axis is for, and the two are only equal by coincidence.
 */
export function fitWindow(
  from: number,
  to: number,
  lastBar: number,
): { from: number; to: number } | undefined {
  // `from` is deliberately not clamped upward: a window may legitimately start
  // before the data, and `fitYRange` already floors it at zero.
  if (lastBar < 0 || from > lastBar) return undefined;
  return { from, to: Math.min(to, lastBar) };
}

/**
 * A fitted range widened so its data occupies only part of the pane.
 *
 * `{from: 0, to: 0.2}` leaves the series in the bottom fifth with the rest
 * clear — volume under candles, on a hidden axis sharing the price pane.
 *
 * **The widening goes on the axis, not on the series.** The alternative is
 * scaling the values, which would make the axis lie about what a bar is worth
 * and make the value legend read a number nobody has. Here the bars keep their
 * own units and only the window around them grows.
 *
 * ```
 * total = span / (to - from)
 * min   = fitted.min - from * total
 * ```
 *
 * This is SciChart's `growBy` arrived at from the other side, and it has to be
 * computed here because `growBy` is applied when SciChart *computes* a range —
 * these axes are `EAutoRange.Never` and get theirs handed to them, so setting
 * `growBy` on one does nothing at all. That is the same trap `setRightMargin`
 * documents for the X axis.
 */
export function bandedRange(
  fitted: YRange,
  band?: { from: number; to: number },
): YRange {
  if (!band) return fitted;
  const height = band.to - band.from;
  // A zero or inverted band has no slice to draw into, and dividing by it would
  // hand the axis an infinite range — every series then collapses to a line.
  if (!(height > 0) || height > 1) return fitted;
  const span = fitted.max - fitted.min;
  // A flat series has no span to scale; widening zero leaves zero, so the
  // fitted range's own padding is all there is to keep.
  if (!(span > 0)) return fitted;
  const total = span / height;
  const min = fitted.min - band.from * total;
  return { min, max: min + total };
}

/**
 * What the price axis is scaled to.
 *
 * `all` is every plot on the axis, which is what an auto-ranged axis has always
 * meant. `price` is the bars alone — the candles' own highs and lows — and the
 * overlays are drawn wherever they fall, clipped if they fall outside.
 *
 * Named for the choice rather than for the mechanism, like `zoomAxis`: the user
 * is picking what the scale is *about*, not naming a data kind.
 */
export type PriceFit = "all" | "price";

/** One series as the fit sees it: its extent, and whether it is the bars. */
export interface YFitCandidate extends YSeriesValues {
  /**
   * True for an OHLC-fed series — the price itself.
   *
   * `dataKind` is the discriminator rather than a plot's name or its position,
   * because it is the one fact that is already true of the data: a candlestick
   * and an OHLC bar are both the price, and a moving average is not, whatever
   * the plot is called.
   */
  isPrice: boolean;
}

/**
 * The series a pane's Y fit should read, given the setting.
 *
 * **`price` is ignored when the axis has no price on it.** An indicator pane —
 * RSI, MACD, volume — has no OHLC series, so filtering to price would leave the
 * fit nothing to measure and the axis would be left at whatever it was. Falling
 * back to every series means the setting is about the price pane by
 * construction, and no caller has to remember to check the pane's role.
 *
 * That is also what makes the setting safe to apply chart-wide: it can only
 * change the one axis it is about.
 */
export function seriesForFit(
  candidates: readonly YFitCandidate[],
  fit: PriceFit,
): readonly YSeriesValues[] {
  if (fit !== "price") return candidates;
  const priced = candidates.filter((c) => c.isPrice);
  return priced.length ? priced : candidates;
}

/** What {@link fitsAxis} reads off a rendered plot. */
export interface YFitMember {
  /** A signal draws in lane units; its values are not prices. */
  signal?: unknown;
  /** A paint plot's +1/-1 colours candles and is not a price either. */
  paint?: unknown;
  hidden?: boolean;
  yAxisId: string | undefined;
}

/**
 * Whether a plot counts toward its axis's Y fit.
 *
 * **A hidden plot does not.** The fit is of what is *drawn*: an A−D column
 * hidden beside Adv and Dec kept its −209 in the range, and the pane carried a
 * band of empty space below zero for a series nobody could see.
 */
export function fitsAxis(member: YFitMember, axisId: string): boolean {
  return !member.signal && !member.paint && !member.hidden && member.yAxisId === axisId;
}
